import { API_BASE_URL } from '../utils/api';

async function parseJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function fetchGameConfig() {
  const res = await fetch(`${API_BASE_URL}/api/game-config`);
  if (!res.ok) throw new Error('game-config');
  return parseJson(res);
}

export async function fetchFlowById(flowId) {
  const res = await fetch(`${API_BASE_URL}/api/flows/${encodeURIComponent(flowId)}`);
  if (!res.ok) throw new Error('flow');
  return parseJson(res);
}

/** Same endpoint as admin AI Request — game chat uses playerId, nickname, prompt (incl. history), characterData, kinksData, selectedGift. */
export async function postGrokRequest(body) {
  const res = await fetch(`${API_BASE_URL}/api/grok/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data?.message || 'AI request failed');
    err.details = data;
    throw err;
  }
  return data;
}

/** Populated scene where `flowId`, `flow2Id`, or `flow3Id` matches, or null if none (404). */
export async function fetchSceneByFlowId(flowId) {
  const id = String(flowId || '').trim();
  if (!id) return null;
  const res = await fetch(`${API_BASE_URL}/api/scenes/by-flow/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    console.warn('[fetchSceneByFlowId]', res.status);
    return null;
  }
  return parseJson(res);
}

export async function lookupUserState(playerId) {
  const q = new URLSearchParams({ playerId: String(playerId).trim() });
  const res = await fetch(`${API_BASE_URL}/api/userstate/lookup?${q}`);
  if (!res.ok) throw new Error('lookup');
  return parseJson(res);
}

export async function patchUserState(body) {
  const res = await fetch(`${API_BASE_URL}/api/userstate/set`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('userstate set');
  return parseJson(res);
}

export async function deleteUserStateOnServer(playerId) {
  const res = await fetch(`${API_BASE_URL}/api/userstate/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ playerId: String(playerId).trim() })
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data?.message || 'userstate delete');
  return data;
}

export async function fetchKinks() {
  const res = await fetch(`${API_BASE_URL}/api/kinks`);
  if (!res.ok) throw new Error('kinks');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchTriggers() {
  const res = await fetch(`${API_BASE_URL}/api/triggers`);
  if (!res.ok) throw new Error('triggers');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchCharacters() {
  const res = await fetch(`${API_BASE_URL}/api/characters`);
  if (!res.ok) throw new Error('characters');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchSets() {
  const res = await fetch(`${API_BASE_URL}/api/sets`);
  if (!res.ok) throw new Error('sets');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchItems() {
  const res = await fetch(`${API_BASE_URL}/api/items`);
  if (!res.ok) throw new Error('items');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchSetById(setId) {
  const res = await fetch(`${API_BASE_URL}/api/sets/${encodeURIComponent(String(setId).trim())}`);
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data?.message || 'set');
  return data;
}

export async function incrementCharacterSession(playerId, characterId) {
  const res = await fetch(`${API_BASE_URL}/api/userstate/increment-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      playerId: String(playerId).trim(),
      characterId: String(characterId).trim()
    })
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data?.message || 'increment session');
  return data;
}

export async function fetchCurrencyPacks() {
  const res = await fetch(`${API_BASE_URL}/api/currency-packs`);
  if (!res.ok) throw new Error('currency-packs');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

/** Flow location assets (Hotel backdrop); newest first from API — caller may use `[0]` as default. */
export async function fetchLocations() {
  const res = await fetch(`${API_BASE_URL}/api/locations`);
  if (!res.ok) throw new Error('locations');
  const data = await parseJson(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchRealItems() {
  const res = await fetch(`${API_BASE_URL}/api/real-items`);
  return parseJson(res);
}

export async function redeemPromo(playerId, code) {
  const res = await fetch(`${API_BASE_URL}/api/promo/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ playerId: String(playerId).trim(), code: String(code).trim() })
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data?.message || 'Promo failed');
    err.code = data?.error;
    throw err;
  }
  return data;
}
